import React, { useState, useEffect, useRef } from 'react'
import { useParams } from 'react-router-dom'
import { useSessionStateStore } from '@renderer/stores/sessionState'
import { useConfigStore } from '@renderer/stores/configState'
import { useCountdown } from '@renderer/utils/countdownUtils'
import { useInterval } from '@renderer/utils/useInterval'
import cogoToast from '@successtar/cogo-toast'
import Preview from '@renderer/components/Preview'
import Spinner from '@renderer/components/Spinner'
import { flashEffect } from '@renderer/utils/flashUtils'
import { unifiedCameraUtils } from '@renderer/utils/cameras/uniCamUtils'
import { filterUtils } from '@renderer/utils/filterUtils'
import { applyLUTThenOverlay } from '@renderer/utils/lutUtils'
import { useSettingsStore } from '@renderer/stores/settingsStore'
import { usePrintStore } from '@renderer/stores/printStore'
import { PrintUtils } from '@renderer/utils/printUtils'
import MidtransUtils from '@renderer/utils/midtransUtils'
import PrintPreview, { PrintPreviewRef } from '@renderer/components/PrintPreview'
import { FrameOption } from '@renderer/types/configState'
import { FaMoneyBillWave } from 'react-icons/fa'
import { useCameraStore } from '@renderer/stores/cameraStore'
import { useCachedBackgroundImage } from '@renderer/hooks/useCachedBackgroundImage'
import { capabilities } from '@renderer/lib/runtime/capabilities'
import { printThermalEscpos, shouldUseThermalPrint } from '@renderer/lib/runtime/thermalPrint'
import { blobToBase64DataUrl, exportThermalImageBlob } from '@renderer/utils/exportThermalImage'
import BahelaThermalSettings from './BahelaThermalSettings'

// Import new Bahela assets
import background from '/assets/bahela-karcis/background.webp'
import karcisKenanganBig from '/assets/bahela-karcis/karcis-kenangan-big.webp'
import mulaiSesi from '/assets/bahela-karcis/mulai-sesi.webp'
import bahela from '/assets/bahela-karcis/bahela.webp'
import karcisKenanganSmall from '/assets/bahela-karcis/karcis-kenangan-small.webp'
import pratinjau from '/assets/bahela-karcis/pratinjau.webp'
import bayar from '/assets/bahela-karcis/bayar.webp'
import pemutarFoto from '/assets/bahela-karcis/pemutar-foto.webp'
import diproses from '/assets/bahela-karcis/diproses.webp'
import pemutarMedia from '/assets/bahela-karcis/pemutar-media.webp'
import silahkanDisobek from '/assets/bahela-karcis/silahkan-disobek.webp'

type Stage = 1 | 2 | 3 | 4 | 5

interface Transaction {
  orderId: string | null
  qrUrl?: string
  expiryTime?: string
  serverTime?: string
  amount: number
  status: string
}

const BahelaReceiptPage: React.FC = () => {
  const { projectId } = useParams()
  const { getFilter, filters, frames, getFrame } = useConfigStore()
  const { id, userPreferences, uploadSessionState } = useSessionStateStore()
  const { cameraSettings, selectedCamera } = useSettingsStore()
  const { getPrinterForFrame } = usePrintStore()
  const { camera, webcam } = useCameraStore()
  const isMounted = useRef(true)

  // States
  const [currentStage, setCurrentStage] = useState<Stage>(1)
  const [remainingTime, setRemainingTime] = useState<string | null>(null)
  const [qrUrl, setQrUrl] = useState<string | null>(null)
  const [isCheckingPayment, setIsCheckingPayment] = useState(false)
  const [capturedImageURL, setCapturedImageURL] = useState<string | null>(null)
  const [captureCountdown, setCaptureCountdown] = useState<number>(10)
  const [transaction, setTransaction] = useState<Transaction>({
    orderId: null,
    amount: 5000,
    status: 'pending'
  })
  const [selectedFrame, setSelectedFrame] = useState<FrameOption | undefined>(undefined)
  const [selectedPhotos, setSelectedPhotos] = useState<string[]>([])
  const [printProgress, setPrintProgress] = useState(0)
  const [isBlinking, setIsBlinking] = useState(false)
  const [thermalSettingsOpen, setThermalSettingsOpen] = useState(false)
  const printPreviewRef = useRef<PrintPreviewRef>(null)

  // Countdown hooks
  const paymentCountdown = useCountdown(120)
  const captureCountdownHook = useCountdown(10)
  const autoStartCountdown = useCountdown(5)
  const resultDisplayCountdown = useCountdown(10)
  const waitCountdown = useCountdown(5)

  const selectedFilter = getFilter(userPreferences.filterId || filters[0]._id)

  // Check if camera is connected and available
  const isCameraConnected = Boolean(selectedCamera && (camera || webcam))

  // Where each stage component is positioned (percentage of container height)
  const COMPONENT_POSITIONS = {
    1: '2%', // Stage 1 component: top = 2% of container
    2: '30%', // Stage 2 component: top = 32% of container
    3: '66%', // Stage 3 component: top = 66% of container
    4: '72%', // Stage 4 component: top = 100% of container
    5: '78%' // Stage 5 component: top = 100% of container
  }

  // Where the viewport moves to show each stage (percentage of container height)
  const VIEW_POSITIONS = {
    1: '0%', // Viewport moves to show stage 1 at 0% of container
    2: '30%', // Viewport moves to show stage 2 at 30% of container
    3: '64%', // Viewport moves to show stage 3 at 64% of container
    4: '70%', // Viewport moves to show stage 4 at 70% of container
    5: '76%' // Viewport moves to show stage 5 at 76% of container
  }

  const calculateRemainingTime = (expiryTime: string, serverTime: string): number => {
    try {
      const expiryTimestamp = new Date(expiryTime).getTime()
      const serverTimestamp = new Date(serverTime).getTime()
      const remainingMs = expiryTimestamp - serverTimestamp
      return Math.max(0, Math.floor(remainingMs / 1000))
    } catch (error) {
      console.error('Error calculating remaining time:', error)
      return 0
    }
  }

  const formatTime = (seconds: number): string => {
    const minutes = Math.floor(seconds / 60)
    const remainingSeconds = seconds % 60
    return `${minutes.toString().padStart(2, '0')}:${remainingSeconds.toString().padStart(2, '0')}`
  }

  const initiatePayment = async (): Promise<void> => {
    try {
      const data = await MidtransUtils.initiatePayment(
        5000,
        id || '',
        120,
        projectId || '',
        userPreferences?.filterId || '',
        userPreferences?.frameId || '',
        0
      )

      if (data.order_id && data.qr_url) {
        const sanitizedQrUrl = data.qr_url.replace(/([^:]\/)\/+/g, '$1')
        const initialRemainingSeconds = calculateRemainingTime(data.expiry_time, data.server_time)

        if (initialRemainingSeconds <= 0) {
          return
        }

        setTransaction({
          orderId: data.order_id,
          qrUrl: sanitizedQrUrl,
          expiryTime: data.expiry_time,
          serverTime: data.server_time,
          status: 'pending',
          amount: 5000
        })

        setQrUrl(sanitizedQrUrl)

        paymentCountdown.setRemainingSeconds(initialRemainingSeconds)
        paymentCountdown.setOnTick(() => {
          setRemainingTime(formatTime(paymentCountdown.getRemainingSeconds()))
        })
        paymentCountdown.setOnComplete(() => handlePaymentExpired())
        paymentCountdown.start()
        setIsCheckingPayment(true)
      }
    } catch (e: unknown) {
      console.error('Error initiating payment:', e)
      cogoToast.error((e as Error).message)
    }
  }

  const handlePaymentExpired = async (): Promise<void> => {
    setIsCheckingPayment(false)
    setRemainingTime('00:00')

    // Cleanup expired payment before initiating new one
    await cleanupPayment()

    setTimeout(() => {
      setQrUrl(null)
      // Reset transaction state before new payment
      setTransaction({
        orderId: null,
        amount: 5000,
        status: 'pending'
      })
      // Only reload QR if still on stage 2 (payment stage)
      if (currentStage === 2) {
        initiatePayment()
      }
    }, 3000)
  }

  const checkPaymentStatus = async (): Promise<void> => {
    if (!transaction?.expiryTime || !transaction?.serverTime) return

    try {
      const paymentData = await MidtransUtils.checkPaymentStatus(
        transaction.orderId || 'order-default-id'
      )

      if (paymentData.isExpired) {
        if (isMounted.current) {
          handlePaymentExpired()
        }
        return
      }

      if (paymentData.paid) {
        setIsCheckingPayment(false)
        setQrUrl(null) // Clear QR URL when payment succeeds
        setTransaction({ ...transaction, status: 'settlement' })
        cogoToast.success('Pembayaran berhasil! Siap-siap foto!')
        // Only advance to stage 3 if we're currently on stage 2 (payment stage)
        if (currentStage === 2) {
          setCurrentStage(3)
          startCaptureCountdown()
        }
      }
    } catch (e: unknown) {
      console.error('Error checking payment status:', e)
    }
  }

  const startCaptureCountdown = (): void => {
    captureCountdownHook.setOnTick(() => {
      setCaptureCountdown(captureCountdownHook.getRemainingSeconds())
    })

    captureCountdownHook.setOnComplete(() => {
      handleCapture()
    })

    captureCountdownHook.start()
  }

  const handleCapture = async (): Promise<void> => {
    try {
      flashEffect('.flash')

      await new Promise((resolve) => setTimeout(resolve, 100))

      const imageData = await unifiedCameraUtils.captureImage(
        userPreferences.selectedCamera as 'primary' | 'secondary',
        applyFilter,
        undefined,
        !cameraSettings.useShutterCapture
      )

      if (imageData) {
        const imageUrl = URL.createObjectURL(imageData)
        setCapturedImageURL(imageUrl)
        setSelectedPhotos([imageUrl])
        const frame = getFrame(userPreferences.frameId || (frames[0]?._id ?? ''))
        setSelectedFrame(frame)

        // Wait 2 seconds then go to printing stage
        setTimeout(() => {
          setCurrentStage(4)
          startPrintingImmediately()
        }, 3000)
      } else {
        cogoToast.error('Gagal mengambil foto, coba lagi!')
        startCaptureCountdown()
      }
    } catch (error) {
      console.error('Capture failed:', error)
      cogoToast.error('Gagal mengambil foto, coba lagi!')
      startCaptureCountdown()
    }
  }

  const startPrintingImmediately = async (): Promise<void> => {
    if (printPreviewRef.current && selectedFrame && selectedPhotos.length > 0) {
      try {
        const composedBlob = await printPreviewRef.current.exportImage()
        const useThermal = await shouldUseThermalPrint()

        if (useThermal) {
          const thermalBlob = await exportThermalImageBlob(composedBlob)
          const base64data = await blobToBase64DataUrl(thermalBlob)
          const jobId = `bahelastruk-${Date.now()}`
          const printResult = await printThermalEscpos({
            jobId,
            imageData: base64data,
            copies: 1,
          })
          if (!printResult.success) {
            cogoToast.error('Gagal mencetak karcis')
          }
        } else {
          const reader = new FileReader()
          reader.onloadend = async (): Promise<void> => {
            const base64data = reader.result as string
            const { printerName, settings } = getPrinterForFrame(
              userPreferences.frameId || (frames[0]?._id ?? '')
            )
            const printResult = await PrintUtils.sendToPrinter(base64data, {
              printerName,
              copies: 1,
              ...settings
            })
            if (!printResult.success) {
              cogoToast.error('Gagal mencetak foto')
            }
          }
          reader.readAsDataURL(composedBlob)
        }
      } catch (error) {
        console.error('Print failed:', error)
        cogoToast.error('Gagal mencetak foto')
      }
    }

    startPrintingProgress()
  }

  const startPrintingProgress = (): void => {
    // Simulate printing progress
    let progress = 0
    const progressInterval = setInterval(() => {
      progress += 2
      setPrintProgress(progress)

      if (progress >= 100) {
        clearInterval(progressInterval)
        setTimeout(() => {
          setCurrentStage(5)
          startResultDisplay()
        }, 1000)
      }
    }, 100)
  }

  const startResultDisplay = (): void => {
    // Start blinking effect
    const blinkInterval = setInterval(() => {
      setIsBlinking((prev) => !prev)
    }, 800)

    resultDisplayCountdown.setOnComplete(() => {
      clearInterval(blinkInterval)
      resetToStage1()
    })
    resultDisplayCountdown.start()
  }

  const cleanupPayment = async (): Promise<void> => {
    if (transaction.orderId && transaction.status !== 'settlement') {
      try {
        await fetch(`${process.env.VITE_API_URL}/api/payments/${transaction.orderId}`, {
          method: 'DELETE',
          credentials: 'include'
        })
        console.log('Payment cancelled:', transaction.orderId)
      } catch (error) {
        console.error('Error cancelling payment:', error)
      }
    }
  }

  const resetToStage1 = (): void => {
    // Cleanup current payment before resetting
    cleanupPayment()

    setCurrentStage(1)
    setCapturedImageURL(null)
    setQrUrl(null)
    setIsCheckingPayment(false)
    setPrintProgress(0)
    setIsBlinking(false)

    // Reset transaction state
    setTransaction({
      orderId: null,
      amount: 5000,
      status: 'pending'
    })

    paymentCountdown.stop()
    captureCountdownHook.stop()
    resultDisplayCountdown.stop()

    // Auto-start session after 5 seconds
    waitCountdown.setOnComplete(() => {
      setCurrentStage(2)
      initiatePayment()
    })
    waitCountdown.start()
  }

  const applyFilter = (ctx: CanvasRenderingContext2D): void => {
    if (selectedFilter?.filterMode === 'lut') {
      applyLUTThenOverlay(ctx, selectedFilter)
    } else if (selectedFilter?.adjustments) {
      filterUtils.applyAdjustments(ctx, selectedFilter.adjustments)
    }
  }

  useEffect(() => {
    uploadSessionState()

    autoStartCountdown.setOnComplete(() => {
      setCurrentStage(2)
      initiatePayment()
    })
    autoStartCountdown.start()

    return (): void => {
      isMounted.current = false
      // Cleanup payment on unmount
      cleanupPayment()
      paymentCountdown.stop()
      captureCountdownHook.stop()
      autoStartCountdown.stop()
      resultDisplayCountdown.stop()
      waitCountdown.stop()
    }
  }, [])

  // Cleanup payment when leaving stage 2
  useEffect(() => {
    if (currentStage !== 2 && transaction.orderId && transaction.status !== 'settlement') {
      cleanupPayment()
      setQrUrl(null)
      setIsCheckingPayment(false)
    }
  }, [currentStage])

  useInterval(
    async () => {
      await checkPaymentStatus()
    },
    isCheckingPayment ? 5000 : null
  )

  // Call all hooks at the top level
  const cachedBackground = useCachedBackgroundImage(background)

  return (
    <div className="relative w-full h-full overflow-hidden">
      {/* Hidden Bypass Payment Button (top left) */}
      <button
        className="absolute top-4 left-4 w-12 h-12 opacity-20 z-50 hover:opacity-40 transition-opacity"
        title="Bypass Payment"
        tabIndex={0}
        onClick={async () => {
          // Cleanup payment when bypassing
          await cleanupPayment()

          setIsCheckingPayment(false)
          setQrUrl(null) // Clear QR URL
          setTransaction((t) => ({ ...t, status: 'settlement' }))

          // Only advance to stage 3 if we're currently on stage 2 (payment stage)
          if (currentStage === 2) {
            setCurrentStage(3)
            startCaptureCountdown()
          }
        }}
      >
        <FaMoneyBillWave className="text-white text-[28px]" />
      </button>

      {/* Hidden PrintPreview for composing photo + frame for printing */}
      <div style={{ display: 'none' }}>
        {selectedFrame && selectedPhotos.length > 0 && (
          <PrintPreview
            ref={printPreviewRef}
            frameConfig={{
              id: selectedFrame._id,
              baseImage: selectedFrame.thumbnail,
              drawParams: selectedFrame.drawParams
            }}
            selectedPhotos={selectedPhotos}
            sessionId={id || ''}
            isInteractive={false}
          />
        )}
      </div>

      {/* Flash effect */}
      <div id="flash" className="w-full h-full flash"></div>

      {/* Fixed Bahela Logo - Bottom Right (Floating) */}
      <img
        src={bahela}
        alt="Bahela Logo"
        className="fixed bottom-8 right-8 w-20 h-14 drop-shadow-2xl z-40"
      />

      {capabilities.thermalPrint && (
        <button
          type="button"
          className="fixed left-4 top-4 z-50 rounded-full bg-white/90 px-3 py-2 text-xs font-medium text-gray-800 shadow-lg"
          onClick={() => setThermalSettingsOpen(true)}
        >
          Printer
        </button>
      )}

      <BahelaThermalSettings
        open={thermalSettingsOpen}
        onClose={() => setThermalSettingsOpen(false)}
      />

      {/* Main Container with Fixed Background */}
      <div
        className="w-full h-[500vh] relative transition-transform duration-1000 ease-in-out"
        style={{
          transform: `translateY(-${VIEW_POSITIONS[currentStage]})`,
          backgroundImage: `url(${cachedBackground})`,
          backgroundSize: '100% 100%',
          backgroundRepeat: 'no-repeat',
          backgroundPosition: 'center top'
        }}
      >
        {/* Stage 1: Welcome Screen */}
        <div
          className={`w-full h-screen flex flex-col items-center justify-center absolute transition-opacity duration-1000 ${
            currentStage === 1 ? 'opacity-100 visible' : 'opacity-0 invisible'
          }`}
          style={{ top: COMPONENT_POSITIONS[1] }}
        >
          <img
            src={karcisKenanganBig}
            alt="Karcis Kenangan"
            className="w-[min(80vw,1200px)] aspect-[866/330] mb-16 drop-shadow-2xl"
          />
          <img
            src={mulaiSesi}
            alt="Mulai Sesi"
            className="w-[min(30vw,500px)] aspect-[364/79] drop-shadow-xl cursor-pointer hover:scale-105 transition-transform"
            onClick={() => {
              setCurrentStage(2)
              initiatePayment()
            }}
          />
        </div>

        {/* Stage 2: Preview & Payment */}
        <div
          className={`w-full h-screen flex flex-col pt-[8vh] px-8 absolute transition-opacity duration-1000 ${
            currentStage === 2 ? 'opacity-100 visible' : 'opacity-0 invisible'
          }`}
          style={{ top: COMPONENT_POSITIONS[2] }}
        >
          {/* Title */}
          <div className="flex justify-center mb-8">
            <img
              src={karcisKenanganSmall}
              alt="Karcis Kenangan"
              className="w-[min(70vw,1000px)] aspect-[768/71] drop-shadow-xl"
            />
          </div>

          {/* Content Container */}
          <div className="flex justify-center gap-16 flex-1">
            {/* Left: Preview Container */}
            <div className="relative flex-1 max-w-[35vw]">
              <img
                src={pratinjau}
                alt="Pratinjau"
                className="w-full h-auto aspect-[357/332] drop-shadow-xl"
              />
              <div className="absolute top-[80px] max-h-[380px] left-0 right-0 bottom-0 p-6">
                {isCameraConnected ? (
                  <Preview
                    key={`preview-${selectedCamera}-${cameraSettings.captureRatio}-${cameraSettings.mirror}-${cameraSettings.previewRotation}-${cameraSettings.previewQuality?.maxWidth}-${cameraSettings.previewQuality?.maxHeight}-${cameraSettings.previewQuality?.quality}-${cameraSettings.previewQuality?.resizeQuality}`}
                    adjustments={selectedFilter?.adjustments}
                    filter={selectedFilter?._id}
                    postLUTAdjustments={selectedFilter?.postLUTAdjustments}
                    ratio={1 / 1}
                    cameraKey={userPreferences.selectedCamera as 'primary' | 'secondary'}
                  />
                ) : (
                  <div className="w-full h-full bg-gray-800 rounded-lg flex flex-col items-center justify-center text-white">
                    <div className="text-center">
                      <div className="text-6xl mb-4">📷</div>
                      <p className="text-xl font-semibold mb-2">Kamera Belum Terhubung</p>
                      <p className="text-sm opacity-75">Silakan hubungkan kamera terlebih dahulu</p>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Right: Payment Container */}
            <div className="relative flex-1 max-w-[60vw]">
              <img
                src={bayar}
                alt="Bayar"
                className="w-full h-auto aspect-[434/404] drop-shadow-xl"
              />
              <div className="absolute top-[120px] left-0 right-0 bottom-0 p-8 text-center">
                {qrUrl ? (
                  <div>
                    <img
                      src={qrUrl}
                      alt="QR Code Pembayaran"
                      className="w-[min(120vw,550px)] aspect-square mx-auto rounded-xl shadow-xl mb-2"
                      crossOrigin="anonymous"
                    />
                    <div className="rounded-lg">
                      <p className="text-[min(5vw,32px)] font-mono font-bold text-gray-800">
                        {remainingTime || '...'}
                      </p>
                    </div>
                  </div>
                ) : (
                  <div className="h-full flex flex-col items-center justify-center gap-4">
                    <Spinner />
                    <p className="text-[18px] font-mono text-gray-700">Loading QR Code...</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Stage 3: Capture Session */}
        <div
          className={`w-full h-screen flex items-center justify-center absolute transition-opacity duration-1000 ${
            currentStage === 3 ? 'opacity-100 visible' : 'opacity-0 invisible'
          }`}
          style={{ top: COMPONENT_POSITIONS[3] }}
        >
          <div className="relative w-[min(80vw,1200px)]">
            <img
              src={pemutarFoto}
              alt="Pemutar Foto"
              className="w-full h-auto aspect-[780/725] drop-shadow-2xl"
            />
            <div className="absolute top-[68px] left-0 right-0 bottom-0 p-12">
              {/* Preview Container - fills almost entire frame */}
              <div className="relative w-full h-[900px] bg-black rounded-lg overflow-hidden">
                {capturedImageURL ? (
                  /* Show captured image immediately after taking photo */
                  <img
                    src={capturedImageURL}
                    alt="Foto Hasil"
                    className="w-full h-full object-cover"
                    crossOrigin="anonymous"
                  />
                ) : isCameraConnected ? (
                  /* Show camera preview during countdown */
                  <Preview
                    adjustments={selectedFilter?.adjustments}
                    filter={selectedFilter?._id}
                    postLUTAdjustments={selectedFilter?.postLUTAdjustments}
                    ratio={1 / 1}
                    cameraKey={userPreferences.selectedCamera as 'primary' | 'secondary'}
                  />
                ) : (
                  /* Show camera not connected message */
                  <div className="w-full h-full flex flex-col items-center justify-center text-white">
                    <div className="text-center">
                      <div className="text-8xl mb-4">📷</div>
                      <p className="text-2xl font-semibold mb-2">Kamera Belum Terhubung</p>
                      <p className="text-lg opacity-75">
                        Silakan hubungkan kamera untuk melanjutkan
                      </p>
                    </div>
                  </div>
                )}

                {/* Countdown Overlay - centered over preview, only show during countdown */}
                {!capturedImageURL && (
                  <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                    <div className="text-[min(15vw,200px)] font-mono font-bold text-white leading-none animate-pulse drop-shadow-2xl bg-black bg-opacity-50 rounded-full w-[200px] h-[200px] flex items-center justify-center">
                      {captureCountdown}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Stage 4: Printing Process */}
        <div
          className={`w-full h-screen flex items-center justify-center absolute transition-opacity duration-1000 ${
            currentStage === 4 ? 'opacity-100 visible' : 'opacity-0 invisible'
          }`}
          style={{ top: COMPONENT_POSITIONS[4] }}
        >
          <div className="relative w-[min(70vw,1000px)]">
            <img
              src={diproses}
              alt="Diproses"
              className="w-full h-auto aspect-[677/286] drop-shadow-2xl"
            />
            <div className="absolute top-[120px] left-0 right-0 bottom-0 p-12">
              <div className="h-full flex flex-col justify-end">
                {/* Progress Bar Container - positioned at bottom */}
                <div className="relative w-full h-[80px] bg-gray-400 rounded-lg overflow-hidden">
                  {/* Progress Bars - individual blue bars with gaps */}
                  {((): JSX.Element[] => {
                    // Calculate how many bars can fit: assume container is ~1000px wide
                    // Each bar: 30px width + 5px gap = 35px, with 10px margins on each side
                    // Available space: 1000 - 20 = 980px
                    // Max bars: 980 / 35 = ~28 bars
                    const maxBars = 28
                    const currentBars = Math.ceil((printProgress / 100) * maxBars)

                    return Array.from({ length: currentBars }, (_, i) => (
                      <div
                        key={i}
                        className="absolute bg-[#287EA1] border-2 border-black rounded-lg"
                        style={{
                          height: '60px',
                          width: '30px',
                          left: `${i * 35 + 10}px`,
                          top: '50%',
                          transform: 'translateY(-50%)'
                        }}
                      />
                    ))
                  })()}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Stage 5: Photo Result */}
        <div
          className={`w-full h-screen flex flex-col items-center justify-center absolute transition-opacity duration-1000 ${
            currentStage === 5 ? 'opacity-100 visible' : 'opacity-0 invisible'
          }`}
          style={{ top: COMPONENT_POSITIONS[5] }}
        >
          <div className="relative mb-4 w-[min(50vw,650px)]">
            <img
              src={pemutarMedia}
              alt="Pemutar Media"
              className="w-full h-auto aspect-[495/646] drop-shadow-2xl"
            />
            <div className="absolute top-[220px] left-0 right-0 bottom-0 p-10 mr-8">
              {capturedImageURL && (
                <img
                  src={capturedImageURL}
                  alt="Foto Hasil"
                  className="w-full h-auto rounded-lg shadow-lg"
                  crossOrigin="anonymous"
                />
              )}
            </div>
          </div>

          <img
            src={silahkanDisobek}
            alt="Silahkan Disobek"
            className={`w-[min(40vw,600px)] aspect-[435/52] transition-opacity duration-700 ${
              isBlinking ? 'opacity-100' : 'opacity-50'
            }`}
          />
        </div>
      </div>

      <style>{`
        .flash {
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: white;
          opacity: 0;
          pointer-events: none;
          z-index: 1000;
        }
        
        .flash.active {
          animation: flash 0.5s ease-out;
        }
        
        @keyframes flash {
          0% { opacity: 0; }
          50% { opacity: 0.8; }
          100% { opacity: 0; }
        }
      `}</style>
    </div>
  )
}

export default BahelaReceiptPage
